// frontend/modules/document-type/components/DocumentTypeForm.tsx
import React, { useEffect } from 'react';
import { Form, Input, InputNumber, Switch, Button, Space, Card, Typography, Row, Col } from 'antd';
import { SaveOutlined, ClearOutlined } from '@ant-design/icons';
import { DocumentTypeFormData, CreateDocumentTypeRequest, UpdateDocumentTypeRequest } from '../types/document-type.types';
import { useDocumentTypeValidation } from '../hooks/useDocumentTypeValidation';
import { FileTypeSelector } from './FileTypeSelector';

const { TextArea } = Input;
const { Title, Text } = Typography;

interface DocumentTypeFormProps {
    initialData?: Partial<DocumentTypeFormData>;
    onSubmit: (data: DocumentTypeFormData) => void;
    onCancel?: () => void;
    loading?: boolean;
    isEdit?: boolean;
}

export const DocumentTypeForm: React.FC<DocumentTypeFormProps> = ({
    initialData,
    onSubmit,
    onCancel,
    loading = false,
    isEdit = false
}) => {
    const [form] = Form.useForm();
    const { validateDocumentType, getFieldError, clearErrors } = useDocumentTypeValidation();

    useEffect(() => {
        if (initialData) {
            form.setFieldsValue(initialData);
        }
    }, [initialData, form]);

    const handleSubmit = async (values: DocumentTypeFormData) => {
        if (validateDocumentType(values)) {
            onSubmit(values);
        }
    };

    const handleReset = () => {
        form.resetFields();
        clearErrors();
    };

    return (
        <Card>
            <Title level={4}>
                {isEdit ? 'Edit Document Type' : 'Create Document Type'}
            </Title>
            
            <Form
                form={form}
                layout="vertical"
                onFinish={handleSubmit}
                initialValues={{
                    isActive: true,
                    isRequired: false,
                    displayOrder: 0,
                    ...initialData
                }}
            >
                <Row gutter={16}>
                    <Col span={12}>
                        <Form.Item
                            name="code"
                            label="Document Code"
                            rules={[
                                { required: true, message: 'Document code is required' },
                                { max: 50, message: 'Document code must not exceed 50 characters' },
                                { pattern: /^[A-Za-z0-9_-]+$/, message: 'Code can only contain letters, numbers, hyphens, and underscores' }
                            ]}
                            validateStatus={getFieldError('code') ? 'error' : ''}
                            help={getFieldError('code')}
                        >
                            <Input
                                placeholder="Enter document code (e.g., ID_CARD, PASSPORT)"
                                disabled={isEdit}
                                maxLength={50}
                            />
                        </Form.Item>
                    </Col>
                    
                    <Col span={12}>
                        <Form.Item
                            name="name"
                            label="Document Name"
                            rules={[
                                { required: true, message: 'Document name is required' },
                                { max: 100, message: 'Document name must not exceed 100 characters' }
                            ]}
                            validateStatus={getFieldError('name') ? 'error' : ''}
                            help={getFieldError('name')}
                        >
                            <Input
                                placeholder="Enter document name (e.g., Identity Card, Passport)"
                                maxLength={100}
                            />
                        </Form.Item>
                    </Col>
                </Row>

                <Form.Item
                    name="description"
                    label="Description"
                    rules={[
                        { max: 500, message: 'Description must not exceed 500 characters' }
                    ]}
                >
                    <TextArea
                        placeholder="Enter document description (optional)"
                        rows={3}
                        maxLength={500}
                        showCount
                    />
                </Form.Item>

                <Row gutter={16}>
                    <Col span={8}>
                        <Form.Item
                            name="minUploadSizeKb"
                            label="Minimum Upload Size (KB)"
                            rules={[
                                { type: 'number', min: 0, message: 'Minimum size must be non-negative' }
                            ]}
                            validateStatus={getFieldError('minUploadSizeKb') ? 'error' : ''}
                            help={getFieldError('minUploadSizeKb')}
                        >
                            <InputNumber
                                placeholder="Min size in KB"
                                style={{ width: '100%' }}
                                min={0}
                            />
                        </Form.Item>
                    </Col>
                    
                    <Col span={8}>
                        <Form.Item
                            name="maxUploadSizeKb"
                            label="Maximum Upload Size (KB)"
                            rules={[
                                { type: 'number', min: 1, message: 'Maximum size must be at least 1 KB' }
                            ]}
                            validateStatus={getFieldError('maxUploadSizeKb') ? 'error' : ''}
                            help={getFieldError('maxUploadSizeKb')}
                        >
                            <InputNumber
                                placeholder="Max size in KB"
                                style={{ width: '100%' }}
                                min={1}
                            />
                        </Form.Item>
                    </Col>
                    
                    <Col span={8}>
                        <Form.Item
                            name="displayOrder"
                            label="Display Order"
                            rules={[
                                { type: 'number', min: 0, message: 'Display order must be non-negative' }
                            ]}
                            validateStatus={getFieldError('displayOrder') ? 'error' : ''}
                            help={getFieldError('displayOrder')}
                        >
                            <InputNumber
                                placeholder="Display order"
                                style={{ width: '100%' }}
                                min={0}
                            />
                        </Form.Item>
                    </Col>
                </Row>

                <Form.Item
                    name="allowedFileTypes"
                    label="Allowed File Types"
                    validateStatus={getFieldError('allowedFileTypes') ? 'error' : ''}
                    help={getFieldError('allowedFileTypes') || 'Leave empty to allow all file types'}
                >
                    <FileTypeSelector placeholder="Select allowed file types (optional)" />
                </Form.Item>

                <Row gutter={16}>
                    <Col span={12}>
                        <Form.Item
                            name="isActive"
                            label="Active Status"
                            valuePropName="checked"
                        >
                            <Switch
                                checkedChildren="Active"
                                unCheckedChildren="Inactive"
                            />
                        </Form.Item>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                            Inactive document types won't be available for selection
                        </Text>
                    </Col>
                    
                    <Col span={12}>
                        <Form.Item
                            name="isRequired"
                            label="Required Status"
                            valuePropName="checked"
                        >
                            <Switch
                                checkedChildren="Required"
                                unCheckedChildren="Optional"
                            />
                        </Form.Item>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                            Required document types must be provided
                        </Text>
                    </Col>
                </Row>

                <Form.Item style={{ marginTop: 24 }}>
                    <Space>
                        <Button
                            type="primary"
                            htmlType="submit"
                            loading={loading}
                            icon={<SaveOutlined />}
                        >
                            {isEdit ? 'Update Document Type' : 'Create Document Type'}
                        </Button>
                        
                        <Button
                            type="default"
                            onClick={handleReset}
                            icon={<ClearOutlined />}
                            disabled={loading}
                        >
                            Reset
                        </Button>
                        
                        {onCancel && (
                            <Button
                                type="default"
                                onClick={onCancel}
                                disabled={loading}
                            >
                                Cancel
                            </Button>
                        )}
                    </Space>
                </Form.Item>
            </Form>
        </Card>
    );
};
